import { useQuery } from '@tanstack/react-query';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Alert, Badge, Button, Card, CardHeader, EmptyState, ErrorState, Input, PageHeader, PageLoader, Table, Td, Th } from '../components/ui';
import { api } from '../lib/api';
import { SUBSCRIPTION_STATUS_LABEL, type StripeAccount, type StripeCustomerRow, type StripeInvoiceRow } from '../lib/billing';
import { formatDate } from '../lib/format';
import { schoolHref } from '../lib/school';

/** Stripe keeps amounts in the smallest unit of the currency. */
const money = (cents: number, currency: string) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits: 2 }).format(cents / 100);

const INVOICE_TONE: Record<string, 'green' | 'amber' | 'red' | 'gray'> = { paid: 'green', open: 'amber', uncollectible: 'red', void: 'gray', draft: 'gray' };

const customerUrl = (id: string) => `https://dashboard.stripe.com/customers/${id}`;

/** Edvora's Stripe account: who it bills, what they owe, and the invoices behind it. Read-only; Stripe does the rest. */
export function PlatformStripePage() {
  const [search, setSearch] = useState('');
  const account = useQuery({ queryKey: ['platform', 'stripe'], queryFn: () => api.get<StripeAccount>('/platform/stripe'), staleTime: 30_000 });
  const data = account.data;
  const term = search.trim().toLowerCase();
  const matches = (...fields: (string | null | undefined)[]) => !term || fields.some((field) => field?.toLowerCase().includes(term));
  const customers = (data?.customers ?? []).filter((customer: StripeCustomerRow) => matches(customer.name, customer.email, customer.school?.name, customer.id));
  const invoices = (data?.invoices ?? []).filter((invoice: StripeInvoiceRow) => matches(invoice.schoolName, invoice.customerName, invoice.number, invoice.id));

  return (
    <>
      <PageHeader
        title="Stripe"
        description="Everyone Edvora bills, what each school owes, and the invoices behind it. Payments, refunds and credits are done in Stripe."
        actions={
          <Button icon={<RefreshCw />} loading={account.isFetching} onClick={() => account.refetch()}>
            Refresh
          </Button>
        }
      />

      {account.isLoading ? (
        <PageLoader />
      ) : account.error ? (
        <ErrorState error={account.error} onRetry={() => account.refetch()} />
      ) : !data?.configured ? (
        <Card>
          <EmptyState
            title="Stripe isn’t set up on this Edvora environment"
            description="Set STRIPE_SECRET_KEY and run the billing setup to create Edvora’s products and prices. Until then, schools can’t subscribe."
          />
        </Card>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-3">
            <Card className="p-4">
              <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">Outstanding</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900 tabular-nums">{money(data.totals.outstanding, data.currency)}</p>
              <p className="text-xs text-slate-500">
                {data.totals.openInvoices} unpaid invoice{data.totals.openInvoices === 1 ? '' : 's'}
              </p>
            </Card>
            <Card className="p-4">
              <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">Customers</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900 tabular-nums">{data.totals.customers}</p>
              <p className="text-xs text-slate-500">{customers.filter((customer) => customer.school).length} matched to a school</p>
            </Card>
            <Card className="p-4">
              <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">In Stripe</p>
              <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline">
                Open the dashboard <ExternalLink className="size-3.5" />
              </a>
              <p className="text-xs text-slate-500">Cards, refunds, credit notes and emails live there.</p>
            </Card>
          </div>

          {data.more && <Alert tone="blue">Showing the most recent 100 customers and invoices. Stripe’s dashboard has the rest.</Alert>}

          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by school, customer, email, or invoice" className="sm:w-96" aria-label="Search" />

          <Card>
            <CardHeader title="Customers" description="Most owed first." />
            {customers.length === 0 ? (
              <EmptyState title="No customers" description={term ? 'None match what you searched for.' : 'Nobody has subscribed yet.'} />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>School</Th>
                    <Th>Customer</Th>
                    <Th>Subscription</Th>
                    <Th className="text-right">Outstanding</Th>
                    <Th className="text-right">Account balance</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {customers.map((customer) => (
                    <tr key={customer.id}>
                      <Td>
                        {customer.school ? (
                          <a href={schoolHref(customer.school.slug)} target="_blank" rel="noreferrer" className="font-medium text-brand-700 hover:underline">
                            {customer.school.name}
                          </a>
                        ) : (
                          <span className="text-slate-500">Not matched to a school</span>
                        )}
                      </Td>
                      <Td>
                        <p className="text-slate-800">{customer.name ?? '—'}</p>
                        <p className="text-xs text-slate-500">{customer.email ?? customer.id}</p>
                      </Td>
                      <Td>
                        {customer.school?.subscriptionStatus ? (
                          <Badge tone={customer.school.subscriptionStatus === 'ACTIVE' ? 'green' : customer.school.subscriptionStatus === 'PAST_DUE' ? 'amber' : 'red'}>
                            {SUBSCRIPTION_STATUS_LABEL[customer.school.subscriptionStatus]}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400">None</span>
                        )}
                        {customer.delinquent && (
                          <Badge tone="red" className="ml-1">
                            Delinquent
                          </Badge>
                        )}
                      </Td>
                      <Td className="text-right tabular-nums">
                        {customer.outstanding > 0 ? (
                          <span className="font-medium text-amber-700">
                            {money(customer.outstanding, customer.currency)}
                            <span className="block text-xs font-normal text-slate-500">
                              {customer.openInvoices} invoice{customer.openInvoices === 1 ? '' : 's'}
                            </span>
                          </span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </Td>
                      <Td className="text-right tabular-nums">
                        {customer.balance === 0 ? <span className="text-slate-400">—</span> : money(customer.balance, customer.currency)}
                      </Td>
                      <Td className="text-right">
                        <a href={customerUrl(customer.id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline">
                          Stripe <ExternalLink className="size-3" />
                        </a>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          <Card>
            <CardHeader title="Invoices" description="Unpaid first, then the most recent." />
            {invoices.length === 0 ? (
              <EmptyState title="No invoices" description={term ? 'None match what you searched for.' : 'Stripe hasn’t billed anyone yet.'} />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Invoice</Th>
                    <Th>School</Th>
                    <Th>Status</Th>
                    <Th className="text-right">Amount</Th>
                    <Th className="text-right">Unpaid</Th>
                    <Th>Date</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <Td className="font-mono text-xs text-slate-600">{invoice.number ?? invoice.id}</Td>
                      <Td>{invoice.schoolName ?? invoice.customerName ?? <span className="text-slate-400">—</span>}</Td>
                      <Td>{invoice.status ? <Badge tone={INVOICE_TONE[invoice.status] ?? 'gray'}>{invoice.status}</Badge> : null}</Td>
                      <Td className="text-right tabular-nums">{money(invoice.amountDue, invoice.currency)}</Td>
                      <Td className="text-right tabular-nums">
                        {invoice.amountRemaining > 0 ? <span className="font-medium text-amber-700">{money(invoice.amountRemaining, invoice.currency)}</span> : <span className="text-slate-400">—</span>}
                      </Td>
                      <Td className="whitespace-nowrap text-slate-500">
                        {invoice.createdAt ? formatDate(invoice.createdAt) : '—'}
                        {invoice.dueAt && invoice.amountRemaining > 0 && <span className="block text-xs">due {formatDate(invoice.dueAt)}</span>}
                      </Td>
                      <Td className="text-right">
                        {invoice.url && (
                          <a href={invoice.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-brand-700 hover:underline">
                            Open <ExternalLink className="size-3" />
                          </a>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </div>
      )}
    </>
  );
}
